import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ScopedStyles } from '../../../store/Layout/home/styled/GlobalStyles';
import Header from '../../../store/Layout/home/components/Header';
import {
  Page,
  MainContent,
  ContentInner,
  Headline,
  Title,
  Subtitle,
  Card,
} from './styled';
import { LoginCard, SignupCard } from './components/AuthContainers';
import {
  getUserInfo1,
  login1,
  verifyGoogleAuth,
  verifyTwoFactor1,
} from '../../../utils/services/user.service';
import { clearMsg, register, verifyOtp, resendOtp } from '../../../redux/actions/authActions';

const isEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

const passwordStrength = (value) => {
  let score = 0;
  if (!value) return 0;
  if (value.length >= 8) score += 1;
  if (value.toLowerCase() !== value && value.toUpperCase() !== value) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  return Math.min(score, 4);
};

const SignIn = ({ initialMode = 'login' }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { message: globalMessage } = useSelector((state) => state.message || {});

  const [mode, setMode] = useState(initialMode);

  // Login state
  const [loginStep, setLoginStep] = useState(1);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [login2fa, setLogin2fa] = useState('');
  const [loginMessage, setLoginMessage] = useState('');
  const [loginPositive, setLoginPositive] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [requiresAuthenticator, setRequiresAuthenticator] = useState(false);
  const [requiresEmailVerification, setRequiresEmailVerification] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const loginCardRef = useRef(null);

  // Signup state
  const [signupStep, setSignupStep] = useState(1);
  const [role, setRole] = useState('wholesaler');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPassword2, setSignupPassword2] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [projectCompany, setProjectCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [signupOtp, setSignupOtp] = useState('');
  const [signupMessage, setSignupMessage] = useState('');
  const [signupPositive, setSignupPositive] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupRecaptchaToken, setSignupRecaptchaToken] = useState(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const passwordScore = useMemo(() => passwordStrength(signupPassword), [signupPassword]);

  useEffect(() => {
    if (mode === 'signup' && globalMessage) {
      const isSuccess = globalMessage.toLowerCase().includes('success');
      setSignupPositive(isSuccess);
      setSignupMessage(globalMessage);
    }
  }, [globalMessage, mode]);

  const resetLoginFlow = () => {
    setLoginStep(1);
    setRequiresOtp(false);
    setRequiresAuthenticator(false);
    setRequiresEmailVerification(false);
    setLoginMessage('');
    setLoginPositive(false);
    setLoginEmail('');
    setLoginPassword('');
    setLoginOtp('');
    setLogin2fa('');
    setLoginLoading(false);
    setRecaptchaToken(null);
    dispatch(clearMsg());
  };

  const resetSignupFields = () => {
    setSignupStep(1);
    setRole('wholesaler');
    setFirstName('');
    setLastName('');
    setSignupEmail('');
    setSignupPassword('');
    setSignupPassword2('');
    setCompanyAddress('');
    setPhoneNumber('');
    setVatNumber('');
    setProjectCompany('');
    setWebsite('');
    setSignupOtp('');
    setSignupMessage('');
    setSignupPositive(false);
    setSignupLoading(false);
    setSignupRecaptchaToken(null);
    setSignupSuccess(false);
    dispatch(clearMsg());
  };

  const handleSwitchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    resetLoginFlow();
    resetSignupFields();
    history.replace(nextMode === 'login' ? '/sign-in' : '/sign-up');
  };

  const handleRoleChange = (value) => {
    setRole(value);
    setFirstName('');
    setLastName('');
    setCompanyAddress('');
    setPhoneNumber('');
    setVatNumber('');
    setProjectCompany('');
    setWebsite('');
    setSignupMessage('');
    setSignupPositive(false);
  };

  const fetchSecurityRequirements = async (emailAddress) => {
    try {
      setLoginLoading(true);
      const response = await getUserInfo1({ email: emailAddress });
      const user = response?.data?.User || {};
      const needOtp = Boolean(user.otp_status);
      const needAuthenticator = !needOtp && Boolean(user.google_auth_status);

      setRequiresEmailVerification(false);
      setRequiresOtp(needOtp);
      setRequiresAuthenticator(needAuthenticator);
      setLoginPositive(false);
      setLoginOtp('');
      setLogin2fa('');
      setRecaptchaToken(null);

      if (needOtp) {
        setLoginStep(3);
        setLoginMessage('Enter the OTP code sent to email.');
      } else if (needAuthenticator) {
        setLoginStep(4);
        setLoginMessage('Enter your 2FA code.');
      } else {
        setLoginMessage('Additional verification required. Please check your email or contact support.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to fetch security requirements.';
      toast.error(message);
      setLoginMessage(message);
      setLoginPositive(false);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLoginNext = async () => {
    if (loginLoading) return;

    setLoginMessage('');
    setLoginPositive(false);

    if (loginStep === 1) {
      if (!isEmail(loginEmail)) {
        setLoginMessage('Enter a valid email.');
        return;
      }
      setRequiresOtp(false);
      setRequiresAuthenticator(false);
      setLoginStep(2);
    setRecaptchaToken(null);
      return;
    }

    if (loginStep === 2) {
      if (loginPassword.length < 8) {
        setLoginMessage('Password must be at least 8 characters.');
        return;
      }

    if (!recaptchaToken) {
      setLoginMessage('Complete the CAPTCHA challenge before continuing.');
      return;
    }

      setLoginLoading(true);
      try {
        const response = await login1({
          email: loginEmail,
          password: loginPassword,
          login_type: 'webapp',
          recaptcha_token: recaptchaToken,
        });

        if (response?.data?.status === 'success') {
          localStorage.setItem('user', JSON.stringify(response.data));
          setLoginPositive(true);
          setLoginMessage('✅ Logged in successfully.');
          window.location.reload();
          history.push('/dashboard');
          return;
        }

        const message = response?.data?.message || 'Unable to log in.';
        setLoginMessage(message);
        toast.error(message);
        // Reset reCAPTCHA on error so user can try again
        if (loginCardRef.current) {
          loginCardRef.current.resetRecaptcha();
        }
      } catch (error) {
        const status = error?.response?.data?.status;
        const errorMessage = error?.response?.data?.message;
        console.log('error------', error);
        console.log('status------', status);
        if(errorMessage == "Missing CAPTCHA token"){
          toast.error('Complete the CAPTCHA challenge before continuing.');
          return;
        }
        
        if(errorMessage == "CAPTCHA verification failed"){
          toast.error('CAPTCHA verification failed. Please try again.');
          // Reset reCAPTCHA on verification failure
          if (loginCardRef.current) {
            loginCardRef.current.resetRecaptcha();
          }
          return;
        }

        if (status === 'not_verified') {
          setRequiresEmailVerification(true);
          setRequiresOtp(true);
          setLoginStep(3);
          setLoginMessage('Please check your email for the verification code.');
          setLoginPositive(false);
          setLoginOtp('');
          return;
        }

        if (status === 'two_factor_auth' || status === 'google_auth') {
          await fetchSecurityRequirements(loginEmail);
          return;
        }

        const message = error?.response?.data?.message || 'Unable to log in. Please try again.';
        setLoginMessage(message);
        toast.error(message);
        // Reset reCAPTCHA on error so user can try again
        if (loginCardRef.current) {
          loginCardRef.current.resetRecaptcha();
        }
      } finally {
        setLoginLoading(false);
        setRecaptchaToken(null);
      }

      return;
    }

    if (loginStep === 3) {
      if (loginOtp.trim().length < 4) {
        setLoginMessage('Enter the OTP code sent to email.');
        return;
      }

      setLoginLoading(true);
      try {
        // Check if this is email verification OTP (not_verified) or 2FA OTP
        if (requiresEmailVerification) {
          // Email verification OTP - use verifyOtp API
          await dispatch(verifyOtp(loginOtp.trim(), loginEmail));
          
          // If verifyOtp succeeds, try logging in again
          try {
            const loginResponse = await login1({
              email: loginEmail,
              password: loginPassword,
              login_type: 'webapp',
              recaptcha_token: recaptchaToken,
            });

            if (loginResponse?.data?.status === 'success') {
              localStorage.setItem('user', JSON.stringify(loginResponse.data));
              setLoginPositive(true);
              setLoginMessage('✅ Logged in successfully.');
              window.location.reload();
              history.push('/dashboard');
              return;
            }
          } catch (loginError) {
            const loginStatus = loginError?.response?.data?.status;
            if (loginStatus === 'two_factor_auth' || loginStatus === 'google_auth') {
              await fetchSecurityRequirements(loginEmail);
              return;
            }
            const message = loginError?.response?.data?.message || 'Unable to log in. Please try again.';
            setLoginMessage(message);
            toast.error(message);
          }
        } else {
          // 2FA OTP - use verifyTwoFactor1 API
          const response = await verifyTwoFactor1({
            otp: loginOtp.trim(),
            email: loginEmail,
            login_type: 'webapp',
          });

          if (response?.data?.status === 'success') {
            localStorage.setItem('user', JSON.stringify(response.data));
            setLoginPositive(true);
            setLoginMessage('✅ Logged in successfully.');
            window.location.reload();
            history.push('/dashboard');
            return;
          }

          const message = response?.data?.message || 'Unable to verify OTP.';
          setLoginMessage(message);
          toast.error(message);
        }
      } catch (error) {
        if (error?.response?.data?.status === 'TFA enabled') {
          setRequiresOtp(false);
          setRequiresAuthenticator(true);
          setRequiresEmailVerification(false);
          setLoginStep(4);
          setLoginPositive(false);
          setLoginMessage(error?.response?.data?.message || 'Enter your 2FA code.');
          setLogin2fa('');
        } else {
          const message = error?.response?.data?.message || 'Unable to verify OTP.';
          setLoginMessage(message);
          toast.error(message);
        }
      } finally {
        setLoginLoading(false);
      }

      return;
    }

    if (loginStep === 4) {
      if (login2fa.trim().length < 6) {
        setLoginMessage('Enter your 2FA code.');
        return;
      }

      setLoginLoading(true);
      try {
        const response = await verifyGoogleAuth({
          token: login2fa.trim(),
          email: loginEmail,
          login_type: 'webapp',
        });

        if (response?.data?.status === 'success') {
          localStorage.setItem('user', JSON.stringify(response.data));
          setLoginPositive(true);
          setLoginMessage('✅ Logged in successfully.');
          history.push('/dashboard');
          return;
        }

        const message = response?.data?.message || 'Unable to verify code.';
        setLoginMessage(message);
        toast.error(message);
      } catch (error) {
        const message = error?.response?.data?.message || 'Unable to verify code.';
        setLoginMessage(message);
        toast.error(message);
      } finally {
        setLoginLoading(false);
      }
    }
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();
    setSignupMessage('');
    setSignupPositive(false);

    if (!role) {
      setSignupMessage('Please select a role.');
      return;
    }
    if (!isEmail(signupEmail.trim())) {
      setSignupMessage('Enter a valid email address.');
      return;
    }
    if (firstName.trim().length < 2) {
      setSignupMessage('Enter your first name.');
      return;
    }
    if (lastName.trim().length < 2) {
      setSignupMessage('Enter your last name.');
      return;
    }
    if (!companyAddress.trim()) {
      setSignupMessage('Enter your complete address.');
      return;
    }
    if (!phoneNumber.trim()) {
      setSignupMessage('Enter your phone number.');
      return;
    }
    if (!vatNumber.trim()) {
      setSignupMessage('Enter your VAT number.');
      return;
    }
    if (signupPassword.length < 8) {
      setSignupMessage('Password must be at least 8 characters.');
      return;
    }
    if (passwordScore < 3) {
      setSignupMessage('Password must be at least GOOD (upper+lower, number or symbol).');
      return;
    }
    if (signupPassword !== signupPassword2) {
      setSignupMessage('Passwords do not match.');
      return;
    }

    if (!signupRecaptchaToken) {
      setSignupMessage('Complete the CAPTCHA challenge before continuing.');
      return;
    }

    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      complete_address: companyAddress.trim(),
      phone_number: phoneNumber.trim(),
      email: signupEmail.trim(),
      password: signupPassword,
      confirmPassword: signupPassword2,
      vat_number: vatNumber.trim(),
      recaptcha_token: signupRecaptchaToken,
    };

    try {
      setSignupLoading(true);
      localStorage.setItem('newUser', JSON.stringify(payload.email));
      await dispatch(register(payload));
      setSignupPositive(true);
      setSignupMessage('✅ Account created. Please check your email for the verification code.');
      setSignupRecaptchaToken(null);
      setSignupStep(2); // Move to OTP step
      dispatch(clearMsg());
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage === 'Missing CAPTCHA token') {
        setSignupMessage('Complete the CAPTCHA challenge before continuing.');
        setSignupRecaptchaToken(null);
        setSignupPositive(false);
        return;
      }
      if (errorMessage === 'CAPTCHA verification failed') {
        setSignupMessage('CAPTCHA verification failed. Please try again.');
        setSignupRecaptchaToken(null);
        setSignupPositive(false);
        return;
      }
      setSignupPositive(false);
      setSignupMessage(globalMessage || 'Unable to sign up. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleSignupOtpVerify = async () => {
    if (signupLoading) return;

    if (signupOtp.trim().length < 4) {
      setSignupMessage('Enter the OTP code sent to your email.');
      setSignupPositive(false);
      return;
    }

    setSignupLoading(true);
    setSignupMessage('');
    setSignupPositive(false);

    try {
      const newUser = JSON.parse(localStorage.getItem('newUser'));
      await dispatch(verifyOtp(signupOtp.trim(), newUser));
      setSignupPositive(true);
      setSignupMessage('✅ Account verified successfully!');
      setSignupSuccess(true);
      setTimeout(() => {
        history.push('/sign-in');
      }, 2000);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Invalid OTP. Please try again.';
      setSignupMessage(errorMessage);
      setSignupPositive(false);
      toast.error(errorMessage);
    } finally {
      setSignupLoading(false);
    }
  };

  const handleSignupResendOtp = async () => {
    try {
      const newUser = JSON.parse(localStorage.getItem('newUser'));
      await dispatch(resendOtp(newUser));
      setSignupPositive(true);
      setSignupMessage('✅ OTP resent. Please check your email.');
      toast.success('OTP resent successfully');
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Unable to resend OTP.';
      setSignupMessage(errorMessage);
      setSignupPositive(false);
      toast.error(errorMessage);
    }
  };

  const handleSignupCancel = () => {
    resetSignupFields();
    history.push('/');
  };

  const loginTotalSteps = Math.max(2, 2 + (requiresOtp ? 1 : 0) + (requiresAuthenticator ? 1 : 0));
  const requireRecaptcha = loginStep === 2;
  const disableNext = requireRecaptcha && !recaptchaToken;
  const handleLoginResendOtp = async () => {
    try {
      await dispatch(resendOtp(loginEmail));
      setLoginPositive(true);
      setLoginMessage('✅ OTP resent. Please check your email.');
      toast.success('OTP resent successfully');
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Unable to resend OTP.';
      setLoginMessage(errorMessage);
      setLoginPositive(false);
      toast.error(errorMessage);
    }
  };

  const handleLoginCancel = () => {
    resetLoginFlow();
    history.push('/');
  };

  return (
    <ScopedStyles>
      <Page>
        <Header />
        <MainContent>
          <ContentInner>
            <Headline>
              <Title>{mode === 'login' ? 'Log In' : 'Create Account'}</Title>
              <Subtitle>
                {mode === 'login'
                  ? 'Secure access with email, OTP and 2FA.'
                  : 'Join PharmaCrowd in a few steps.'}
              </Subtitle>
            </Headline>

            <Card>
              {mode === 'login' ? (
                <LoginCard
                  ref={loginCardRef}
                  mode={mode}
                  onSwitchMode={handleSwitchMode}
                  step={loginStep}
                  totalSteps={loginTotalSteps}
                  email={loginEmail}
                  password={loginPassword}
                  otp={loginOtp}
                  twofa={login2fa}
                  requiresOtp={requiresOtp}
                  requiresAuthenticator={requiresAuthenticator}
                  requiresEmailVerification={requiresEmailVerification}
                  onEmailChange={(event) => setLoginEmail(event.target.value)}
                  onPasswordChange={(event) => setLoginPassword(event.target.value)}
                  onOtpChange={(event) => setLoginOtp(event.target.value)}
                  onTwofaChange={(event) => setLogin2fa(event.target.value)}
                  onNext={handleLoginNext}
                  onCancel={handleLoginCancel}
                  onResendOtp={handleLoginResendOtp}
                  message={loginMessage}
                  messagePositive={loginPositive}
                  loading={loginLoading}
                  onRecaptchaChange={(value) => setRecaptchaToken(value)}
                  onRecaptchaExpired={() => setRecaptchaToken(null)}
                  requireRecaptcha={requireRecaptcha}
                  disableNext={disableNext}
                />
              ) : (
                <SignupCard
                  mode={mode}
                  onSwitchMode={handleSwitchMode}
                  step={signupStep}
                  totalSteps={2}
                  role={role}
                  onRoleChange={(event) => handleRoleChange(event.target.value)}
                  firstName={firstName}
                  lastName={lastName}
                  email={signupEmail}
                  address={companyAddress}
                  phone={phoneNumber}
                  vatNumber={vatNumber}
                  password={signupPassword}
                  password2={signupPassword2}
                  passwordScore={passwordScore}
                  otp={signupOtp}
                  onOtpChange={(event) => setSignupOtp(event.target.value)}
                  onFirstNameChange={(event) => setFirstName(event.target.value)}
                  onLastNameChange={(event) => setLastName(event.target.value)}
                  onEmailChange={(event) => setSignupEmail(event.target.value)}
                  onAddressChange={(event) => setCompanyAddress(event.target.value)}
                  onPhoneChange={(event) => setPhoneNumber(event.target.value)}
                  onVatChange={(event) => setVatNumber(event.target.value)}
                  onPasswordChange={(event) => setSignupPassword(event.target.value)}
                  onPassword2Change={(event) => setSignupPassword2(event.target.value)}
                  message={signupMessage}
                  messagePositive={signupPositive}
                  loading={signupLoading}
                  onSubmit={signupStep === 1 ? handleSignupSubmit : (e) => { e.preventDefault(); handleSignupOtpVerify(); }}
                  onCancel={handleSignupCancel}
                  onResendOtp={handleSignupResendOtp}
                  onRecaptchaChange={(value) => setSignupRecaptchaToken(value)}
                  onRecaptchaExpired={() => setSignupRecaptchaToken(null)}
                  recaptchaToken={signupRecaptchaToken}
                  success={signupSuccess}
                />
              )}
            </Card>
          </ContentInner>
        </MainContent>
      </Page>
    </ScopedStyles>
  );
};

export default SignIn;
